import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { resolveVideoUploadSniffed, resolveThumbnailUpload } from '@/lib/videoUpload';


export interface ReviewMedia {
  id: string;
  review_id: string;
  user_id: string;
  media_type: 'photo' | 'video';
  media_url: string;
  thumbnail_url: string | null;
  display_order: number;
  created_at: string;
}

// Fetch media for a specific review
export const useReviewMedia = (reviewId?: string) => {
  return useQuery({
    queryKey: ['review-media', reviewId],
    queryFn: async () => {
      if (!reviewId) return [];
      
      const { data, error } = await supabase
        .from('review_media')
        .select('*')
        .eq('review_id', reviewId)
        .order('display_order', { ascending: true });
      
      if (error) throw error;
      return data as ReviewMedia[];
    },
    enabled: !!reviewId,
  });
};

// Upload review media
export const useUploadReviewMedia = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({
      reviewId,
      userId,
      file,
      mediaType,
      thumbnailBlob,
      displayOrder,
    }: {
      reviewId: string;
      userId: string;
      file: File | Blob;
      mediaType: 'photo' | 'video';
      thumbnailBlob?: Blob;
      displayOrder: number;
    }) => {
      // Generate unique file path with a browser-friendly extension + Content-Type.
      const timestamp = Date.now();
      const videoMeta = mediaType === 'video' ? await resolveVideoUploadSniffed(file) : null;
      const extension = mediaType === 'video' ? videoMeta!.ext : 'webp';
      const contentType = mediaType === 'video' ? videoMeta!.contentType : ((file as any)?.type || 'image/webp');
      const filePath = `${userId}/${reviewId}/${mediaType}-${timestamp}.${extension}`;
      if (mediaType === 'video') {
        console.info('[VideoPlaybackDebug] upload resolve', {
          reviewId, userId, declared: videoMeta!.declaredType,
          container: videoMeta!.container, ext: extension, contentType,
        });
      }

      // Upload main file
      const { error: uploadError } = await supabase.storage
        .from('review-media')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
          contentType,
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('review-media')
        .getPublicUrl(filePath);

      // Upload thumbnail for videos
      let thumbnailUrl: string | null = null;
      if (thumbnailBlob && mediaType === 'video') {
        const { ext, contentType: thumbType } = resolveThumbnailUpload(thumbnailBlob);
        const thumbPath = `${userId}/${reviewId}/thumb-${timestamp}.${ext}`;
        const { error: thumbError } = await supabase.storage
          .from('review-media')
          .upload(thumbPath, thumbnailBlob, {
            cacheControl: '3600',
            upsert: false,
            contentType: thumbType,
          });


        
        if (!thumbError) {
          const { data: { publicUrl: thumbUrl } } = supabase.storage
            .from('review-media')
            .getPublicUrl(thumbPath);
          thumbnailUrl = thumbUrl;
        }
      }
      
      // Create database record
      const { data, error: insertError } = await supabase
        .from('review_media')
        .insert({
          review_id: reviewId,
          user_id: userId,
          media_type: mediaType,
          media_url: publicUrl,
          thumbnail_url: thumbnailUrl,
          display_order: displayOrder,
        })
        .select()
        .single();
      
      if (insertError) throw insertError;
      
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['review-media', variables.reviewId] });
    },
    onError: (error) => {
      console.error('Failed to upload media:', error);
      toast.error('Failed to upload media');
    },
  });
};

// Delete review media
export const useDeleteReviewMedia = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({
      mediaId,
      mediaUrl,
      thumbnailUrl,
      reviewId,
    }: {
      mediaId: string;
      mediaUrl: string;
      thumbnailUrl?: string | null;
      reviewId: string;
    }) => {
      // Extract file path from URL
      const extractPath = (url: string) => {
        const match = url.match(/review-media\/(.+)$/);
        return match ? match[1] : null;
      };
      
      // Delete from storage
      const mainPath = extractPath(mediaUrl);
      if (mainPath) {
        await supabase.storage.from('review-media').remove([mainPath]);
      }
      
      if (thumbnailUrl) {
        const thumbPath = extractPath(thumbnailUrl);
        if (thumbPath) {
          await supabase.storage.from('review-media').remove([thumbPath]);
        }
      }
      
      // Delete database record
      const { error } = await supabase
        .from('review_media')
        .delete()
        .eq('id', mediaId);
      
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['review-media', variables.reviewId] });
      toast.success('Media deleted');
    },
    onError: (error) => {
      console.error('Failed to delete media:', error);
      toast.error('Failed to delete media');
    },
  });
};

// Batch upload multiple media files for a review
export const uploadReviewMediaBatch = async (
  reviewId: string,
  userId: string,
  mediaFiles: Array<{
    file: File | Blob;
    mediaType: 'photo' | 'video';
    thumbnailBlob?: Blob;
  }>
): Promise<void> => {
  for (let i = 0; i < mediaFiles.length; i++) {
    const { file, mediaType, thumbnailBlob } = mediaFiles[i];

    const timestamp = Date.now() + i;
    const videoMeta = mediaType === 'video' ? await resolveVideoUploadSniffed(file) : null;
    const extension = mediaType === 'video' ? videoMeta!.ext : 'webp';
    const contentType = mediaType === 'video'
      ? videoMeta!.contentType
      : ((file as any)?.type || 'image/webp');
    const filePath = `${userId}/${reviewId}/${mediaType}-${timestamp}.${extension}`;
    if (mediaType === 'video') {
      console.info('[VideoPlaybackDebug] batch upload resolve', {
        reviewId, userId, index: i,
        declared: videoMeta!.declaredType, container: videoMeta!.container,
        ext: extension, contentType,
      });
    }

    // Upload main file with an explicit, browser-friendly Content-Type.
    const { error: uploadError } = await supabase.storage
      .from('review-media')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
        contentType,
      });

    if (uploadError) {
      console.error('Upload error:', uploadError);
      throw uploadError;
    }

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('review-media')
      .getPublicUrl(filePath);

    // Upload thumbnail for videos
    let thumbnailUrl: string | null = null;
    if (thumbnailBlob && mediaType === 'video') {
      const { ext, contentType: thumbType } = resolveThumbnailUpload(thumbnailBlob);
      const thumbPath = `${userId}/${reviewId}/thumb-${timestamp}.${ext}`;
      const { error: thumbError } = await supabase.storage
        .from('review-media')
        .upload(thumbPath, thumbnailBlob, {
          cacheControl: '3600',
          upsert: false,
          contentType: thumbType,
        });

      if (!thumbError) {
        const { data: { publicUrl: thumbUrl } } = supabase.storage
          .from('review-media')
          .getPublicUrl(thumbPath);
        thumbnailUrl = thumbUrl;
      }
    }

    
    // Create database record
    const { error: insertError } = await supabase
      .from('review_media')
      .insert({
        review_id: reviewId,
        user_id: userId,
        media_type: mediaType,
        media_url: publicUrl,
        thumbnail_url: thumbnailUrl,
        display_order: i,
      });
    
    if (insertError) {
      console.error('Insert error:', insertError);
      throw insertError;
    }
  }
};
